import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Bike, ShoppingBag, Truck, Check, Phone, MapPin,
  Clock, RefreshCw, Navigation, DollarSign, ChevronRight, Package, TrendingUp,
  CreditCard, Store, X, Eye, MessageSquare, XCircle, AlertTriangle, Undo2, Smartphone, User,
  ClipboardList,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { IfoodDeliveryCodeDialog } from "../features/ifood";
import { getToken } from "../utils/tokenStorage";

const comLogin = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });
import { formaBase, useNomeForma } from "../features/pedidos/lib/formasDaLoja";
import { useMedidaDoItem } from "../hooks/useMedidaDoItem";
import { qtdDoItem } from "../lib/unidadeVenda";
import { motivoValido } from "../utils/motivoCancelamento";

// Ícone do WhatsApp customizado
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Logo Nucleo como ícone
const NucleoLogoIcon = ({ className }) => (
  <img src="/logo-nucleo.png" alt="Nucleo" className={className} style={{ objectFit: 'contain' }} />
);

const API = '/api';

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('data:') || url.startsWith('http')) return url;
  return url.startsWith('/') ? `/api${url}` : `/api/${url}`;
};

function formatPhoneNumber(phone) {
  if (!phone) return '';
  const digits = phone.replace(/\D/g, '');
  if (digits.length === 11) {
    return `(${digits.slice(0,2)})${digits[2]}.${digits.slice(3,7)}-${digits.slice(7)}`;
  } else if (digits.length === 10) {
    return `(${digits.slice(0,2)})${digits.slice(2,6)}-${digits.slice(6)}`;
  }
  return phone;
}

function getModuloIcon(modulo) {
  const mod = (modulo || '').toLowerCase().trim();
  switch(mod) {
    case 'delivery':
      return { Icon: User, label: 'Novo Pedido', color: 'text-blue-500' };
    case 'cardapio':
    case 'cardapiopublico':
      return { Icon: NucleoLogoIcon, label: 'Cardápio Digital', color: '' };
    case 'whatsapp':
      return { Icon: WhatsAppIcon, label: 'WhatsApp', color: 'text-green-500' };
    case 'balcao':
      return { Icon: Store, label: 'Balcão', color: 'text-orange-500' };
    case 'comandas':
      return { Icon: ClipboardList, label: 'Comanda', color: 'text-amber-500' };
    case 'totem':
      return { Icon: Store, label: 'Totem', color: 'text-violet-500' };
    default:
      return null;
  }
}

export default function EntregadorDetalhe() {
  const { entregadorId } = useParams();
  const navigate = useNavigate();

  const [entregador, setEntregador] = useState(null);
  // Após descontinuação da etapa 'na_bag', mantemos a chave no estado para que
  // pedidos legados ainda sejam exibidos junto dos em_rota na mesma lista.
  const [pedidos, setPedidos] = useState({ na_bag: [], em_rota: [] });
  const [stats, setStats] = useState({ entregas_hoje: 0, faturamento: 0 });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("em_rota");
  const [pedidoDetalhes, setPedidoDetalhes] = useState(null);
  const nomeForma = useNomeForma(); // código da forma é interno: mostra o nome
  // Quilo/litro (30/09/2026): item por peso sai "1,250 kg" (medida pelo cadastro).
  const medidaItem = useMedidaDoItem();
  const [detalhesModalOpen, setDetalhesModalOpen] = useState(false);
  const [nowTick, setNowTick] = useState(Date.now());
  const [cancelarModalOpen, setCancelarModalOpen] = useState(false);
  const [pedidoParaCancelar, setPedidoParaCancelar] = useState(null);
  const [motivoCancelamento, setMotivoCancelamento] = useState("");

  useEffect(() => {
    if (!detalhesModalOpen) return;
    const iv = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(iv);
  }, [detalhesModalOpen]);

  useEffect(() => {
    // Esconder menu lateral e abas superiores em mobile
    const sidebar = document.querySelector('aside');
    const header = document.querySelector('header');
    if (window.innerWidth < 768) {
      if (sidebar) sidebar.style.display = 'none';
      if (header) header.style.display = 'none';
    }
    return () => {
      if (sidebar) sidebar.style.display = '';
      if (header) header.style.display = '';
    };
  }, []);

  const handleVoltar = () => {
    navigate('/delivery');
  };

  const fetchData = useCallback(async () => {
    try {
      // Login da loja em todas as chamadas (achado 23, 27/10/2026): o backend
      // passou a exigir; antes estas iam sem o token
      const [entregadorRes, pedidosRes, statsRes] = await Promise.all([
        axios.get(`${API}/entregadores/${entregadorId}`, comLogin()),
        axios.get(`${API}/entregadores/${entregadorId}/pedidos`, comLogin()),
        axios.get(`${API}/entregadores/${entregadorId}/stats`, comLogin())
      ]);

      setEntregador(entregadorRes.data);
      setPedidos({
        na_bag: pedidosRes.data.filter(p => p.status === 'na_bag'),
        em_rota: pedidosRes.data.filter(p => p.status === 'em_rota')
      });
      setStats(statsRes.data || { entregas_hoje: 0, faturamento: 0 });
    } catch (error) {
      console.error("Erro ao carregar dados:", error);
      toast.error("Erro ao carregar dados do entregador");
    } finally {
      setLoading(false);
    }
  }, [entregadorId]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  // Helper de atualização otimista: move pedido entre as listas na_bag/em_rota imediatamente,
  // faz rollback se o request falhar. Elimina a latência perceptível nas transições.
  const updatePedidoLocal = (pedidoId, novoStatus) => {
    setPedidos(prev => {
      const todos = [...prev.na_bag, ...prev.em_rota];
      const idx = todos.findIndex(p => p.id === pedidoId);
      if (idx === -1) return prev;
      const atualizado = { ...todos[idx], status: novoStatus };
      const outros = todos.filter(p => p.id !== pedidoId);
      return {
        na_bag: [
          ...(novoStatus === 'na_bag' ? [atualizado] : []),
          ...outros.filter(p => p.status === 'na_bag'),
        ],
        em_rota: [
          ...(novoStatus === 'em_rota' ? [atualizado] : []),
          ...outros.filter(p => p.status === 'em_rota'),
        ],
      };
    });
  };
  const removePedidoLocal = (pedidoId) => {
    setPedidos(prev => ({
      na_bag: prev.na_bag.filter(p => p.id !== pedidoId),
      em_rota: prev.em_rota.filter(p => p.id !== pedidoId),
    }));
  };
  const findPedidoLocal = (pedidoId) => {
    return pedidos.na_bag.find(p => p.id === pedidoId) || pedidos.em_rota.find(p => p.id === pedidoId);
  };
  const restorePedidoLocal = (pedido) => {
    setPedidos(prev => ({
      na_bag: pedido.status === 'na_bag' ? [pedido, ...prev.na_bag.filter(p => p.id !== pedido.id)] : prev.na_bag,
      em_rota: pedido.status === 'em_rota' ? [pedido, ...prev.em_rota.filter(p => p.id !== pedido.id)] : prev.em_rota,
    }));
  };

  const handleEnviarParaRota = async (pedidoId) => {
    const prev = findPedidoLocal(pedidoId);
    updatePedidoLocal(pedidoId, 'em_rota');
    toast.success("Pedido enviado para rota!");
    try {
      await axios.patch(`${API}/pedidos/${pedidoId}/status?status=em_rota`, {}, comLogin());
    } catch (error) {
      if (prev) restorePedidoLocal(prev);
      toast.error("Erro ao enviar para rota");
    }
  };

  // iFood com "código de entrega": o cliente recebeu um código no app; o entregador
  // digita antes de concluir (o Núcleo valida no iFood). Vale para o botão do card
  // e para o "próximo status" do modal.
  const [ifoodCodeDialog, setIfoodCodeDialog] = useState(null);
  const precisaCodigoEntregaIfood = (p) => {
    const m = p && p.ifood_meta && typeof p.ifood_meta === 'object' ? p.ifood_meta : {};
    return (p?.modulo || '').toLowerCase() === 'ifood' && !!m.delivery_code_required && !m.delivery_code_verified_at;
  };

  const handleConcluirEntrega = async (pedidoId, codigoOk = false) => {
    const prev = findPedidoLocal(pedidoId);
    if (!codigoOk && precisaCodigoEntregaIfood(prev)) {
      setIfoodCodeDialog({ pedido: prev, proceed: () => handleConcluirEntrega(pedidoId, true) });
      return;
    }
    removePedidoLocal(pedidoId); // pedido concluído sai da lista
    toast.success("Entrega concluída!");
    try {
      await axios.patch(`${API}/pedidos/${pedidoId}/status?status=concluido`, {}, comLogin());
      // Atualiza stats depois (não bloqueia UX)
      fetchData();
    } catch (error) {
      if (prev) restorePedidoLocal(prev);
      toast.error("Erro ao concluir entrega");
    }
  };

  const handleVoltarParaPronto = async (pedidoId) => {
    const prev = findPedidoLocal(pedidoId);
    removePedidoLocal(pedidoId); // pronto sai do escopo do entregador
    toast.success("Pedido voltou para Pronto!");
    try {
      await axios.patch(`${API}/pedidos/${pedidoId}/status?status=pronto`, {}, comLogin());
    } catch (error) {
      if (prev) restorePedidoLocal(prev);
      toast.error("Erro ao voltar pedido");
    }
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  };

  const calcularTempoDecorrido = (dateStr) => {
    if (!dateStr) return "";
    const now = new Date();
    const created = new Date(dateStr);
    const diffMs = now - created;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "agora";
    if (diffMin < 60) return `${diffMin}min`;
    const h = Math.floor(diffMin / 60);
    const m = diffMin % 60;
    return `${h}h${m > 0 ? m + 'min' : ''}`;
  };

  const handleVerDetalhes = async (pedido) => {
    try {
      const res = await axios.get(`${API}/pedidos/${pedido.id}`, comLogin());
      setPedidoDetalhes(res.data);
      setDetalhesModalOpen(true);
    } catch (error) {
      // Use the pedido data we already have
      setPedidoDetalhes(pedido);
      setDetalhesModalOpen(true);
    }
  };

  const statusConfig = {
    aguardando_aceite: { label: 'Aguardando', color: 'bg-yellow-500' },
    producao: { label: 'Produção', color: 'bg-blue-500' },
    pronto: { label: 'Pronto', color: 'bg-green-500' },
    na_bag: { label: 'Na Bag', color: 'bg-indigo-500' },
    em_rota: { label: 'Em Rota', color: 'bg-purple-500' },
    concluido: { label: 'Concluído', color: 'bg-emerald-500' },
    cancelado: { label: 'Cancelado', color: 'bg-red-500' },
  };

  const podeVoltarEtapa = (status) => {
    return !['aguardando_aceite', 'producao', 'cancelado'].includes(status);
  };

  const getProximoStatus = (status, retirada) => {
    if (retirada) {
      return { 'aguardando_aceite': 'producao', 'producao': 'pronto', 'pronto': 'concluido' }[status];
    }
    // Fluxo simplificado: na_bag foi descontinuado → pronto vai direto pra em_rota.
    // 'na_bag' → 'em_rota' mantido só pra avançar pedidos legados presos.
    return { 'aguardando_aceite': 'producao', 'producao': 'pronto', 'pronto': 'em_rota', 'na_bag': 'em_rota', 'em_rota': 'concluido' }[status];
  };

  const getStatusAnterior = (status) => {
    // Sem etapa na_bag — em_rota volta direto pra pronto. 'na_bag': 'pronto' fica pra legados.
    return { 'pronto': 'producao', 'na_bag': 'pronto', 'em_rota': 'pronto', 'concluido': 'em_rota' }[status];
  };

  const handleMudarStatus = async (pedido, novoStatus, codigoOk = false) => {
    if (!pedido || !novoStatus || pedido.status === novoStatus) return;
    if (novoStatus === 'concluido' && !codigoOk && precisaCodigoEntregaIfood(pedido)) {
      setIfoodCodeDialog({ pedido, proceed: () => handleMudarStatus(pedido, novoStatus, true) });
      return;
    }
    try {
      await axios.patch(`${API}/pedidos/${pedido.id}/status?status=${novoStatus}`, {},
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success(`Status alterado para: ${statusConfig[novoStatus]?.label || novoStatus}`);
      if (pedidoDetalhes && pedidoDetalhes.id === pedido.id) {
        setPedidoDetalhes({ ...pedidoDetalhes, status: novoStatus });
      }
      fetchData();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Erro ao alterar status do pedido");
    }
  };

  const handleAbrirCancelar = (pedido, e) => {
    if (e) e.stopPropagation();
    setPedidoParaCancelar(pedido);
    setMotivoCancelamento("");
    setCancelarModalOpen(true);
  };

  const handleCancelarPedido = async () => {
    if (!pedidoParaCancelar) return;
    if (!motivoValido(motivoCancelamento)) {
      toast.error("Informe o motivo do cancelamento");
      return;
    }
    try {
      // Login junto: a rota /cancelar exige usuário (sem ele voltava 403).
      await axios.patch(`${API}/pedidos/${pedidoParaCancelar.id}/cancelar`, { motivo: motivoCancelamento.trim() },
        { headers: { Authorization: `Bearer ${getToken()}` } });
      toast.success("Pedido cancelado!");
      setCancelarModalOpen(false);
      setPedidoParaCancelar(null);
      setMotivoCancelamento("");
      setDetalhesModalOpen(false);
      fetchData();
    } catch (error) {
      const motivo = error?.response?.data?.detail;
      toast.error(typeof motivo === "string" ? motivo : "Erro ao cancelar pedido");
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  };

  const isRetirada = (p) => {
    const t = (p.tipo_entrega || '').toLowerCase().trim();
    return t === 'pickup' || t === 'retirada';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen w-full">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!entregador) {
    return (
      <div className="flex flex-col items-center justify-center h-screen w-full p-8">
        <Bike className="w-16 h-16 text-muted-foreground/30 mb-4" />
        <p className="text-muted-foreground mb-6 text-lg">Entregador não encontrado</p>
        <Button onClick={handleVoltar} size="lg">
          <ArrowLeft className="w-5 h-5 mr-2" />
          Voltar para Delivery
        </Button>
      </div>
    );
  }

  const totalPedidos = pedidos.na_bag.length + pedidos.em_rota.length;

  // Card do pedido - mesmo estilo do Delivery
  const PedidoCard = ({ pedido, actionButton, onViewDetails }) => {
    const retirada = isRetirada(pedido);
    const tempoDecorrido = calcularTempoDecorrido(pedido.created_at);

    const enderecoCompleto = retirada ? null : [
      pedido.endereco_rua,
      pedido.endereco_numero ? `nº ${pedido.endereco_numero}` : null,
      pedido.endereco_bairro,
      pedido.endereco_complemento
    ].filter(Boolean).join(', ') || "Sem endereço";

    return (
      <div className={`rounded-lg border-l-4 border p-2.5 sm:p-3 shadow-sm transition-all relative overflow-hidden ${
        retirada
          ? 'bg-amber-50 dark:bg-amber-950/20 border-l-amber-500 border-amber-400/50'
          : 'bg-card border-l-orange-500 border-border/50'
      }`}>
        {/* Linha 1: código + nome + telefone | impressora + hora + items */}
        <div className="flex items-center gap-1.5 mb-1">
          {retirada && (
            <span className="bg-amber-500 text-white text-[9px] px-1.5 py-0.5 rounded font-bold flex items-center gap-0.5 shrink-0">
              <Store className="w-3 h-3" /> RETIRADA
            </span>
          )}
          {/* Ícone do módulo de origem */}
          {(() => {
            const iconInfo = getModuloIcon(pedido.modulo);
            if (!iconInfo) return null;
            const { Icon, label, color } = iconInfo;
            return (
              <span className={`${color} shrink-0`} title={label}>
                <Icon className="w-3.5 h-3.5" />
              </span>
            );
          })()}
          <span className="font-mono text-sm sm:text-base font-bold text-orange-500">{pedido.codigo}</span>
          <span className="font-semibold text-xs sm:text-sm truncate">{pedido.cliente_nome || "Cliente"}</span>
          {pedido.cliente_telefone && (
            <a
              href={`https://wa.me/55${pedido.cliente_telefone.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-[10px] sm:text-xs text-green-500 hover:text-green-400 hover:underline shrink-0"
            >
              <Phone className="w-3 h-3" />
              {formatPhoneNumber(pedido.cliente_telefone)}
            </a>
          )}
          <div className="flex items-center gap-1.5 ml-auto shrink-0">
            <div className="text-right leading-tight">
              <span className="text-xs text-muted-foreground">{formatTime(pedido.created_at)}</span>
              <span className="text-[10px] text-muted-foreground ml-1">{pedido.items?.length || 0} item(s)</span>
            </div>
            <span className="text-[10px] text-orange-500 font-medium">{tempoDecorrido}</span>
          </div>
        </div>

        {/* Linha 2: endereço */}
        <div className="text-[10px] sm:text-xs text-muted-foreground mb-1.5">
          {retirada ? (
            <span className="flex items-center gap-1">
              <Store className="w-3 h-3" /> Retirada no local
            </span>
          ) : (
            <span className="flex items-start gap-1">
              <MapPin className="w-3 h-3 mt-0.5 flex-shrink-0" />
              <span className="line-clamp-1">{enderecoCompleto}</span>
            </span>
          )}
        </div>

        {/* Linha 3: preço + pagamento + botão */}
        <div className="flex items-center gap-2">
          <span className="font-bold text-orange-500 text-sm sm:text-base">R$ {(pedido.total || 0).toFixed(2).replace(".", ",")}</span>
          {pedido.forma_pagamento && (
            <span className="flex items-center gap-1 text-[10px] sm:text-xs text-muted-foreground">
              <CreditCard className="w-3 h-3" />
              {nomeForma(pedido.forma_pagamento)}
              {formaBase(pedido.forma_pagamento) === 'dinheiro' && pedido.troco_valor && (
                <span className="text-green-600 font-medium ml-0.5">(troco p/ R${Number(pedido.troco_valor).toFixed(2).replace(".", ",")})</span>
              )}
            </span>
          )}
          <div className="ml-auto flex items-center gap-1.5">
            <button
              onClick={() => onViewDetails && onViewDetails(pedido)}
              className="p-1.5 rounded-md bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 hover:bg-blue-200 dark:hover:bg-blue-800/40 transition-colors"
              title="Ver detalhes do pedido"
            >
              <Eye className="w-4 h-4" />
            </button>
            {actionButton}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen w-full flex flex-col bg-background overflow-hidden">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-background border-b shadow-sm flex-shrink-0">
        <div className="px-2 py-2 sm:p-3">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleVoltar} className="h-9 px-2 sm:px-3">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1 text-sm">Voltar</span>
            </Button>
            <div className="flex items-center gap-1.5 min-w-0 flex-1">
              <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center shrink-0">
                <Bike className="w-3.5 h-3.5 text-primary" />
              </div>
              <span className="font-bold text-sm truncate">{entregador.nome}</span>
              {entregador.telefone && (
                <a href={`tel:${entregador.telefone}`} className="text-[10px] text-muted-foreground hover:text-primary shrink-0">
                  <Phone className="w-3 h-3" />
                </a>
              )}
            </div>
            {/* Stats compactos */}
            <div className="flex items-center gap-1 shrink-0">
              <div className="flex items-center gap-0.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 px-1.5 py-0.5 rounded text-[10px] font-bold">
                <Package className="w-3 h-3" /> {stats.entregas_hoje}
              </div>
              <div className="flex items-center gap-0.5 bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 px-1.5 py-0.5 rounded text-[10px] font-bold">
                <TrendingUp className="w-3 h-3" /> R${stats.faturamento.toFixed(0)}
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={fetchData} className="h-9 w-9 shrink-0">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Aba única "Em Rota" — etapa 'Na Bag' foi descontinuada, pedidos legados
          nesse status ficam no mesmo bloco aqui e podem ser avançados pelo entregador. */}
      <div className="flex border-b bg-muted/30 shrink-0">
        <div className="flex-1 py-2 text-xs sm:text-sm font-semibold text-center text-purple-600 dark:text-purple-400 relative">
          <div className="flex items-center justify-center gap-1.5">
            <Truck className="w-4 h-4" />
            <span>Em Rota</span>
            <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400">
              {pedidos.em_rota.length + pedidos.na_bag.length}
            </span>
          </div>
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-purple-500" />
        </div>
      </div>

      {/* Conteúdo */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-2 sm:p-3 space-y-2 max-w-2xl mx-auto w-full">
          {(() => {
            const todos = [...pedidos.em_rota, ...pedidos.na_bag];
            if (todos.length === 0) {
              return (
                <div className="p-8 text-center text-muted-foreground">
                  <Truck className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">Nenhum pedido em rota</p>
                </div>
              );
            }
            return todos.map(pedido => {
              const isLegadoNaBag = pedido.status === 'na_bag';
              return (
                <PedidoCard
                  key={pedido.id}
                  pedido={pedido}
                  onViewDetails={handleVerDetalhes}
                  actionButton={
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleVoltarParaPronto(pedido.id)}
                        className="p-1.5 rounded-md hover:bg-muted transition-colors"
                        title="Voltar para Pronto"
                      >
                        <Undo2 className="w-3.5 h-3.5 text-muted-foreground" />
                      </button>
                      {isLegadoNaBag ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleEnviarParaRota(pedido.id)}
                          className="border-purple-500/50 text-purple-600 hover:bg-purple-500/10 h-8 text-xs"
                          title="Avançar legado Na Bag → Em Rota"
                        >
                          <Truck className="w-3.5 h-3.5 mr-1" /> Em Rota
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          onClick={() => handleConcluirEntrega(pedido.id)}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 text-xs"
                        >
                          <Check className="w-3.5 h-3.5 mr-1" /> Entregue
                        </Button>
                      )}
                    </div>
                  }
                />
              );
            });
          })()}
        </div>
      </div>

      {/* Modal de Detalhes do Pedido */}
      <Dialog open={detalhesModalOpen} onOpenChange={setDetalhesModalOpen}>
        <DialogContent className="max-w-[100vw] sm:max-w-[95vw] md:max-w-lg w-full h-[100dvh] sm:h-auto sm:max-h-[90vh] flex flex-col m-0 sm:m-4 rounded-none sm:rounded-lg p-0">
          {pedidoDetalhes && (
            <>
              {/* Header */}
              <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-lg font-bold">{pedidoDetalhes.codigo}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium text-white ${statusConfig[pedidoDetalhes.status]?.color || 'bg-gray-500'}`}>
                      {statusConfig[pedidoDetalhes.status]?.label || pedidoDetalhes.status}
                    </span>
                    {isRetirada(pedidoDetalhes) && (
                      <span className="bg-amber-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold flex items-center gap-0.5">
                        <Store className="w-3 h-3" /> RETIRADA
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatDate(pedidoDetalhes.created_at)} às {formatTime(pedidoDetalhes.created_at)}
                  </p>
                </div>
              </div>

              {/* Conteúdo scrollável */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {/* Cancelamento */}
                {pedidoDetalhes.status === 'cancelado' && pedidoDetalhes.motivo_cancelamento && (
                  <div className="bg-red-50 dark:bg-red-950/20 rounded-lg p-2.5 border border-red-200 dark:border-red-800">
                    <p className="text-xs font-semibold text-red-700 dark:text-red-400 flex items-center gap-1 mb-0.5">
                      <AlertTriangle className="w-3.5 h-3.5" /> Cancelamento
                    </p>
                    <p className="text-sm">{pedidoDetalhes.motivo_cancelamento}</p>
                  </div>
                )}

                {/* Cliente + Endereço */}
                <div className="bg-muted/30 rounded-lg p-2.5 space-y-2">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm">{pedidoDetalhes.cliente_nome || "Não informado"}</p>
                    {pedidoDetalhes.cliente_telefone && (
                      <a
                        href={`https://wa.me/55${pedidoDetalhes.cliente_telefone.replace(/\D/g, '')}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xs text-green-600 hover:underline flex items-center gap-1 mt-0.5"
                      >
                        <Phone className="w-3 h-3" /> {formatPhoneNumber(pedidoDetalhes.cliente_telefone)}
                      </a>
                    )}
                  </div>
                  {!isRetirada(pedidoDetalhes) && (pedidoDetalhes.endereco_rua || pedidoDetalhes.endereco_bairro) && (
                    <div className="flex items-start gap-1.5 pt-1.5 border-t border-muted">
                      <MapPin className="w-3.5 h-3.5 mt-0.5 text-primary shrink-0" />
                      <div className="text-xs">
                        <p className="font-medium">
                          {pedidoDetalhes.endereco_rua}{pedidoDetalhes.endereco_numero && `, ${pedidoDetalhes.endereco_numero}`}
                        </p>
                        {pedidoDetalhes.endereco_bairro && <p className="text-muted-foreground">{pedidoDetalhes.endereco_bairro}</p>}
                        {pedidoDetalhes.endereco_complemento && <p className="text-muted-foreground italic">{pedidoDetalhes.endereco_complemento}</p>}
                        {pedidoDetalhes.endereco_referencia && <p className="text-muted-foreground">Ref: {pedidoDetalhes.endereco_referencia}</p>}
                      </div>
                    </div>
                  )}
                </div>

                {/* Etapas com tempo decorrido */}
                <div className="bg-muted/30 rounded-lg p-2.5">
                  {(() => {
                    const isPickup = isRetirada(pedidoDetalhes);
                    const p = pedidoDetalhes;
                    const steps = isPickup
                      ? [
                          { label: 'Aceite', ts: p.created_at },
                          { label: 'Produção', ts: p.ts_producao },
                          { label: 'Pronto', ts: p.ts_pronto },
                          { label: 'Retirado', ts: p.ts_concluido },
                        ]
                      : [
                          { label: 'Aceite', ts: p.created_at },
                          { label: 'Produção', ts: p.ts_producao },
                          { label: 'Pronto', ts: p.ts_pronto },
                          // 'Na Bag' descontinuado — só exibido para pedidos legados que gravaram ts_na_bag
                          ...(p.ts_na_bag ? [{ label: 'Na Bag', ts: p.ts_na_bag }] : []),
                          { label: 'Em Rota', ts: p.ts_em_rota },
                          { label: 'Entregue', ts: p.ts_concluido },
                        ];
                    const fmtElapsed = (ms) => {
                      if (!ms || ms < 0) return '';
                      const totalSec = Math.floor(ms / 1000);
                      if (totalSec < 60) return `${totalSec}s`;
                      const min = Math.floor(totalSec / 60);
                      const sec = totalSec % 60;
                      if (min < 60) return sec > 0 ? `${min}min ${sec}s` : `${min}min`;
                      const h = Math.floor(min / 60);
                      const m = min % 60;
                      return m > 0 ? `${h}h ${m}min` : `${h}h`;
                    };
                    let totalMs = 0;
                    const stepsWithElapsed = steps.map((s, i) => {
                      let diffMs = 0;
                      let isLive = false;
                      if (s.ts) {
                        const nextTs = (i < steps.length - 1 && steps[i + 1]?.ts) ? new Date(steps[i + 1].ts).getTime() : null;
                        if (nextTs) {
                          diffMs = nextTs - new Date(s.ts).getTime();
                        } else if (i < steps.length - 1) {
                          // Cancelado: congela no instante do cancelamento em vez de contar ao vivo.
                          const fimTs = p.status === 'cancelado' ? new Date(p.ts_cancelado || p.updated_at).getTime() : null;
                          diffMs = (fimTs ?? nowTick) - new Date(s.ts).getTime();
                          isLive = !fimTs;
                        }
                        if (diffMs > 0) totalMs += diffMs;
                      }
                      return { ...s, diffMs, isLive };
                    });

                    return (
                      <>
                        <div className="flex flex-wrap gap-x-3 gap-y-1 text-[10px]">
                          {stepsWithElapsed.map((s, i) => (
                            <span key={i} className={`flex items-center gap-0.5 ${s.ts ? 'text-foreground font-medium' : 'text-muted-foreground'}`}>
                              {s.ts ? <Check className="w-3 h-3 text-primary" /> : <span className="w-3 h-3 rounded-full border border-muted-foreground/30 inline-block" />}
                              {s.label}
                              {s.diffMs > 0 && (
                                <span className={`font-normal ml-0.5 ${s.isLive ? 'text-green-500 font-mono' : 'text-orange-500'}`}>
                                  {fmtElapsed(s.diffMs)}
                                </span>
                              )}
                            </span>
                          ))}
                        </div>
                        {totalMs > 0 && (
                          <div className="flex items-center gap-1 mt-1.5 pt-1.5 border-t border-muted text-[10px] text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            <span>Tempo total: <span className="font-semibold text-foreground">{fmtElapsed(totalMs)}</span></span>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>

                {/* Itens */}
                <div>
                  <p className="text-xs font-semibold mb-1.5 flex items-center gap-1.5 text-muted-foreground">
                    <Package className="w-3.5 h-3.5" /> Itens ({pedidoDetalhes.items?.length || 0})
                  </p>
                  <div className="divide-y divide-muted">
                    {pedidoDetalhes.items && pedidoDetalhes.items.length > 0 ? (
                      pedidoDetalhes.items.map((item, index) => {
                        const photoUrl = item.photo_url || item.foto_url;
                        const photoSrc = photoUrl ? getImageUrl(photoUrl) : null;
                        const qtd = item.quantidade || item.qty || item.quantity || 1;
                        return (
                          <div key={index} className="py-2 first:pt-0">
                            <div className="flex gap-2 items-start">
                              {photoSrc && (
                                <img src={photoSrc} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" onError={(e) => { e.target.style.display = "none"; }} />
                              )}
                              <div className="flex-1 min-w-0">
                                <div className="flex justify-between items-start">
                                  <p className="text-sm font-medium">
                                    {qtdDoItem(qtd, medidaItem(item))} {item.nome || item.name}
                                  </p>
                                  <p className="text-sm font-medium shrink-0 ml-2">
                                    R$ {((item.preco_unitario || item.preco || item.price || 0) * qtd).toFixed(2).replace(".", ",")}
                                  </p>
                                </div>
                                {item.subitems && item.subitems.length > 0 && (
                                  <div className="mt-0.5">
                                    {item.subitems.map((sub, subIdx) => (
                                      <p key={subIdx} className="text-[10px] text-muted-foreground leading-relaxed">
                                        <span className="text-orange-400">+ </span>
                                        {(sub.step_name || sub.stepName) && <span className="opacity-70">{sub.step_name || sub.stepName}: </span>}
                                        {sub.nome || sub.name}
                                        {(sub.preco > 0) && <span className="text-orange-500"> (+R$ {sub.preco.toFixed(2).replace(".", ",")})</span>}
                                      </p>
                                    ))}
                                  </div>
                                )}
                                {item.etapas && item.etapas.length > 0 && (
                                  <div className="mt-0.5">
                                    {item.etapas.flatMap((etapa, etapaIdx) => {
                                      const etapaItems = etapa.items || etapa.itens || [];
                                      return etapaItems.map((etapaItem, itemIdx) => {
                                        const itemNome = typeof etapaItem === 'string' ? etapaItem : (etapaItem.nome || etapaItem.name || '');
                                        return (
                                          <p key={`${etapaIdx}-${itemIdx}`} className="text-[10px] text-muted-foreground leading-relaxed">
                                            <span className="text-orange-400">+ </span>{itemNome}
                                          </p>
                                        );
                                      });
                                    })}
                                  </div>
                                )}
                                {item.observacao && (
                                  <p className="text-[10px] text-yellow-700 dark:text-yellow-400 mt-0.5 italic">* {item.observacao}</p>
                                )}
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-sm text-muted-foreground py-2">Nenhum item</p>
                    )}
                  </div>
                </div>

                {/* Observação */}
                {pedidoDetalhes.observacao && (
                  <div className="bg-yellow-50 dark:bg-yellow-950/20 rounded-lg p-2.5 border border-yellow-200 dark:border-yellow-800">
                    <p className="text-xs font-semibold text-yellow-700 dark:text-yellow-400 flex items-center gap-1 mb-0.5">
                      <MessageSquare className="w-3.5 h-3.5" /> Observação
                    </p>
                    <p className="text-sm">{pedidoDetalhes.observacao}</p>
                  </div>
                )}
              </div>

              {/* Footer */}
              <div className="border-t px-4 py-3 shrink-0">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col gap-0.5">
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>{pedidoDetalhes.forma_pagamento ? nomeForma(pedidoDetalhes.forma_pagamento) : "Não definido"}</span>
                      {formaBase(pedidoDetalhes.forma_pagamento) === 'dinheiro' && pedidoDetalhes.troco_valor && (
                        <span className="text-green-600">(troco p/ R${Number(pedidoDetalhes.troco_valor).toFixed(2).replace(".", ",")})</span>
                      )}
                    </div>
                    {pedidoDetalhes.valor_entrega > 0 && (
                      <span className="text-[10px] text-muted-foreground ml-5">Frete: R$ {Number(pedidoDetalhes.valor_entrega).toFixed(2).replace(".", ",")}</span>
                    )}
                  </div>
                  <span className="font-bold text-primary text-lg">R$ {(pedidoDetalhes.total || 0).toFixed(2).replace(".", ",")}</span>
                </div>
                {/* Controles de Status */}
                {pedidoDetalhes.status !== 'cancelado' && pedidoDetalhes.status !== 'concluido' && (
                  <div className="flex gap-2 mt-2">
                    {podeVoltarEtapa(pedidoDetalhes.status) && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-9 text-xs"
                        onClick={() => {
                          const statusAnterior = getStatusAnterior(pedidoDetalhes.status);
                          if (statusAnterior) handleMudarStatus(pedidoDetalhes, statusAnterior);
                        }}
                      >
                        <Undo2 className="w-3.5 h-3.5 mr-1" /> Voltar
                      </Button>
                    )}
                    {(() => {
                      const proximoStatus = getProximoStatus(pedidoDetalhes.status, isRetirada(pedidoDetalhes));
                      if (!proximoStatus) return null;
                      return (
                        <Button
                          size="sm"
                          className="flex-1 h-9 text-xs"
                          onClick={() => handleMudarStatus(pedidoDetalhes, proximoStatus)}
                        >
                          <ChevronRight className="w-3.5 h-3.5 mr-1" /> {statusConfig[proximoStatus]?.label || proximoStatus}
                        </Button>
                      );
                    })()}
                  </div>
                )}
                {/* Botão Cancelar */}
                {pedidoDetalhes.status !== 'cancelado' && pedidoDetalhes.status !== 'concluido' && (
                  <div className="flex gap-2 mt-2">
                    <Button
                      variant="destructive"
                      size="sm"
                      className="flex-1 h-9 text-xs"
                      onClick={(e) => handleAbrirCancelar(pedidoDetalhes, e)}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1" /> Cancelar
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <IfoodDeliveryCodeDialog
        pedido={ifoodCodeDialog?.pedido}
        open={!!ifoodCodeDialog}
        onClose={() => setIfoodCodeDialog(null)}
        onVerified={() => { const d = ifoodCodeDialog; setIfoodCodeDialog(null); d?.proceed?.(); }}
        onSkip={() => { const d = ifoodCodeDialog; setIfoodCodeDialog(null); d?.proceed?.(); }}
      />

      {/* Modal de Cancelamento */}
      <Dialog open={cancelarModalOpen} onOpenChange={setCancelarModalOpen}>
        <DialogContent className="max-w-[95vw] sm:max-w-md w-full m-2 sm:m-4">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Cancelar Pedido
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {pedidoParaCancelar && (
              <div className="p-3 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-800">
                <p className="font-mono text-sm">{pedidoParaCancelar.codigo}</p>
                <p className="font-semibold">{pedidoParaCancelar.cliente_nome}</p>
                <p className="text-lg font-bold text-primary mt-1">
                  R$ {(pedidoParaCancelar.total || 0).toFixed(2).replace(".", ",")}
                </p>
              </div>
            )}

            <div>
              <Label className="text-sm font-semibold">
                Motivo do Cancelamento <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={motivoCancelamento}
                onChange={(e) => setMotivoCancelamento(e.target.value)}
                placeholder="Informe o motivo do cancelamento..."
                className="mt-2 min-h-[100px]"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Este motivo será visível para o cliente no rastreio do pedido.
              </p>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setCancelarModalOpen(false)} className="flex-1">
                Voltar
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelarPedido}
                disabled={!motivoValido(motivoCancelamento)}
                className="flex-1"
              >
                <XCircle className="w-4 h-4 mr-1" />
                Confirmar Cancelamento
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
